import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getSavedJobIds, removeSavedJob } from '../../services/jobService';
import JobList from './JobList';
import LoadingSpinner from '../UI/LoadingSpinner';
import { parseDate } from '../../utils/parsers';

/**
 * Saved Jobs Page component
 */
const SavedJobsPage = () => {
  const { user } = useAuth();
  
  // State
  const [allJobs, setAllJobs] = useState([]);
  const [savedJobIds, setSavedJobIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeJobId, setActiveJobId] = useState(null);
  const [sortOption, setSortOption] = useState('datecloses_asc'); // Default sort: date closes (soonest first)
  
  // Load all jobs once
  useEffect(() => {
    const fetchAllJobs = async () => {
      try {
        const response = await fetch("/clean.JSON");
        if (!response.ok) {
          throw new Error(`Failed to load jobs: ${response.status}`);
        }
        
        const data = await response.json();
        setAllJobs(data);
      } catch (err) {
        setError(`Failed to load jobs: ${err.message}`);
      }
    };
    
    fetchAllJobs();
  }, []);
  
  // Load saved job IDs whenever user changes
  useEffect(() => {
    const fetchSavedJobIds = async () => {
      if (!user) {
        setSavedJobIds([]);
        setLoading(false);
        return;
      }
      
      try {
        const ids = await getSavedJobIds(user.id);
        setSavedJobIds(ids);
      } catch (err) {
        setError(`Error loading saved jobs: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSavedJobIds();
  }, [user]);
  
  // Handle removing a job
  const handleRemoveJob = async (jobId) => {
    if (!user) return;
    
    setActiveJobId(jobId);
    
    try {
      const success = await removeSavedJob(user.id, jobId);
      
      if (success) {
        // Update local state
        setSavedJobIds(prev => prev.filter(id => id !== jobId));
      } else {
        setError('Failed to remove job');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setActiveJobId(null);
    }
  };
  
  // Get saved jobs with full data and apply sorting
  const savedJobs = useMemo(() => {
    // Filter jobs to only include saved ones
    const filtered = allJobs.filter(job => savedJobIds.some(id => id == job.cis_fuss_id));
    
    // Apply sorting based on selected option
    return [...filtered].sort((a, b) => {
      // Handle date posted sorting
      if (sortOption === "dateposted_desc" || sortOption === "dateposted_asc") {
        const dateA = parseDate(a.datejobposted);
        const dateB = parseDate(b.datejobposted);
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        return sortOption === "dateposted_desc" ? dateB - dateA : dateA - dateB;
      }
      
      // Handle date closes sorting
      if (sortOption === "datecloses_desc" || sortOption === "datecloses_asc") {
        const dateA = parseDate(a.datecloses);
        const dateB = parseDate(b.datecloses);
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        return sortOption === "datecloses_desc" ? dateB - dateA : dateA - dateB;
      }
      
      // Handle title sorting
      if (sortOption === "title_asc" || sortOption === "title_desc") {
        return sortOption === "title_asc"
          ? a.title.localeCompare(b.title)
          : b.title.localeCompare(a.title);
      }
      
      // Handle organization sorting
      if (sortOption === "org_asc" || sortOption === "org_desc") {
        return sortOption === "org_asc"
          ? a.organization.localeCompare(b.organization)
          : b.organization.localeCompare(a.organization);
      }
      
      // Handle CV similarity sorting
      if (sortOption === "cv_similarity") {
        const simA = a.cv_similarity !== undefined ? a.cv_similarity : 0;
        const simB = b.cv_similarity !== undefined ? b.cv_similarity : 0;
        
        return simB - simA; // Highest first
      }
      
      return 0;
    });
  }, [allJobs, savedJobIds, sortOption]);
  
  // Style
  const containerStyle = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '20px',
  };
  
  const sectionStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  };
  
  const errorStyle = {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '10px 15px',
    borderRadius: '5px',
    marginBottom: '15px',
  };
  
  const emptyStateStyle = {
    padding: '30px',
    textAlign: 'center',
    color: '#666',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px dashed #ccc',
  };
  
  return (
    <div style={containerStyle}>
      {error && (
        <div style={errorStyle} role="alert">
          {error}
        </div>
      )}
      
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0 }}>
            <span style={{ color: "#2980B9" }}>Your Saved Jobs</span>
          </h1>
          
          <div>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              style={{ 
                padding: '8px', 
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            >
              <option value="datecloses_asc">📅 Date Closes (Soonest)</option>
              <option value="datecloses_desc">📅 Date Closes (Latest)</option>
              <option value="dateposted_desc">🗓️ Date Posted (Newest first)</option>
              <option value="dateposted_asc">🗓️ Date Posted (Oldest first)</option>
              <option value="title_asc">🔤 Title (A-Z)</option>
              <option value="title_desc">🔤 Title (Z-A)</option>
              <option value="org_asc">🏢 Organization (A-Z)</option>
              <option value="org_desc">🏢 Organization (Z-A)</option>
              <option value="cv_similarity">🤖 CV Similarity (Highest first)</option>
            </select>
          </div>
        </div>
        
        {loading ? (
          <LoadingSpinner />
        ) : savedJobs.length === 0 ? (
          <div style={emptyStateStyle}>
            <h3>No Saved Jobs</h3>
            <p>Jobs you save will appear here for easy reference.</p>
          </div>
        ) : (
          <>
            <p style={{ margin: '0 0 15px 0' }}><strong>{savedJobs.length}</strong> saved {savedJobs.length === 1 ? 'job' : 'jobs'}</p>
            <JobList 
              jobs={savedJobs}
              savedJobIds={savedJobIds}
              onRemoveJob={handleRemoveJob}
              showSaveButtons={false}
              savingJobId={activeJobId}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SavedJobsPage;